import { useEffect, useRef } from 'react';

interface PixelProps {
  pixelId: string;
  dataProcessingCookies: boolean;
}

export const GTMScript = ({ pixelId, dataProcessingCookies }: PixelProps) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        // match valid pixel id: GTM- followed by 5+ alphanumeric chars
        if (!trimmedId?.match(/^GTM-[A-Z0-9]{5,}$/) || !dataProcessingCookies) {
          return;
        }

        // Prevent duplicate script injection
        if (document.getElementById('gtm-pixel')) {
          return;
        }

        const script = document.createElement('script');
        script.id = 'gtm-pixel';
        script.innerHTML = `
          (function(){try{
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${trimmedId}');
          }catch{/*swallow exception*/}})();
        `;
        document.head.appendChild(script);
        scriptRef.current = script;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (scriptRef.current?.parentNode) {
          scriptRef.current.parentNode.removeChild(scriptRef.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [pixelId, dataProcessingCookies]);

  return null;
};

export const GTMPixel = ({ pixelId }: PixelProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        if (!trimmedId) {
          return;
        }

        // Prevent duplicate iframe injection
        if (document.getElementById('gtm-iframe')) {
          return;
        }

        const iframe = document.createElement('iframe');
        iframe.id = 'gtm-iframe';
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${trimmedId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        document.head.appendChild(iframe);
        iframeRef.current = iframe;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (iframeRef.current?.parentNode) {
          iframeRef.current.parentNode.removeChild(iframeRef.current);
        }
      } catch {
        // Iframe already removed, safe to ignore
      }
    };
  }, [pixelId]);

  return null;
};
